// src/components/Chat/ChatWindow.tsx
// Jendela chat — UI/UX optimized untuk overlay di atas peta

import React, { useState } from "react";
import { ChatMessage, NoiseSummary } from "../../types/chatTypes";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import NoiseSummaryBar from "./NoiseSummaryBar";

interface ChatWindowProps {
  messages: ChatMessage[];
  isLoading: boolean;
  noiseSummary: NoiseSummary;
  onSend: (text: string) => void;
  onClose: () => void;
  onClear: () => void;
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  isLoading,
  noiseSummary,
  onSend,
  onClose,
  onClear,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);

  return (
    <div
      style={{
        position: "fixed",
        top: 12,
        right: 72,
        width: 350,
        maxWidth: "calc(100vw - 160px)",
        height: isMinimized ? "auto" : "min(520px, calc(100vh - 24px))",
        borderRadius: 16,
        background: "#1e2139",
        boxShadow: "0 10px 50px rgba(0,0,0,0.5), 0 0 0 1px rgba(74,144,226,0.2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 99999,
        animation: "chatSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        fontFamily: "'Segoe UI', -apple-system, sans-serif",
      }}
    >
      <style>{`
        @keyframes chatSlideIn {
          0% { opacity: 0; transform: translateY(-10px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Header — premium look */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          background: "#252941",
          color: "white",
          flexShrink: 0,
          cursor: "pointer",
          borderBottom: "1px solid rgba(74,144,226,0.15)",
        }}
        onClick={() => setIsMinimized(!isMinimized)}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.2, color: "#ffffff" }}>EcoNoise Assistant</div>
            <div style={{ fontSize: 11, opacity: 0.6, lineHeight: 1.2, color: "#a0a8c0" }}>AI Chatbot Kebisingan</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
          <button
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            title="Hapus percakapan"
            style={{
              background: "rgba(74,144,226,0.15)", border: "none", color: "#4a90e2",
              width: 30, height: 30, borderRadius: 8, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", outline: "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.15)"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setIsMinimized(!isMinimized); }}
            title={isMinimized ? "Perbesar" : "Perkecil"}
            style={{
              background: "rgba(74,144,226,0.15)", border: "none", color: "#4a90e2",
              width: 30, height: 30, borderRadius: 8, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", outline: "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.15)"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {isMinimized ? <polyline points="17 11 12 6 7 11" /> : <polyline points="7 13 12 18 17 13" />}
            </svg>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            title="Tutup chat"
            style={{
              background: "rgba(74,144,226,0.15)", border: "none", color: "#4a90e2",
              width: 30, height: 30, borderRadius: 8, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center", outline: "none",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.25)"}
            onMouseLeave={(e) => e.currentTarget.style.background = "rgba(74,144,226,0.15)"}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Body — hidden when minimized */}
      {!isMinimized && (
        <>
          {/* Noise Summary Bar */}
          <NoiseSummaryBar summary={noiseSummary} />

          {/* Messages */}
          <MessageList messages={messages} />

          {/* Input */}
          <ChatInput onSend={onSend} isLoading={isLoading} />
        </>
      )}
    </div>
  );
};

export default ChatWindow;
